import type { PublicUser, ServerCapabilities } from '@adhd/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, UNAUTHORIZED_EVENT } from './api.js';

/**
 * Session and server-capability context.
 *
 * Capabilities are loaded once at boot so the UI can hide what the deployment
 * cannot do, rather than offering a button that fails. Running with no AI keys
 * is a supported configuration, and it should look intentional — not broken.
 */

interface SessionValue {
  user: PublicUser | null;
  capabilities: ServerCapabilities | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** Set when the device has been in another zone long enough to be worth asking about. */
  travel: { zone: string; since: string } | null;
  setTravel: (t: { zone: string; since: string } | null) => void;
  setUser: (user: PublicUser | null) => void;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [capabilities, setCapabilities] = useState<ServerCapabilities | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /* Raised only after a different zone has persisted — see noteTravel. */
  const [travel, setTravel] = useState<{ zone: string; since: string } | null>(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const [session, caps] = await Promise.all([api.session(), api.capabilities()]);
      setUser(session.user);
      setTravel(session.travel);
      setCapabilities(caps);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reach the server.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // The shared page has no owner and no session. Firing the session and
    // capabilities requests there put two round trips in front of a visitor who
    // has no stake in either, and a failure on either one — /capabilities runs
    // a COUNT over the users table — replaced a share that would have loaded
    // fine with "Can't reach the server". Skip the bootstrap entirely and let
    // that page fetch the one thing it actually needs.
    if (window.location.pathname.startsWith('/shared/')) {
      setLoading(false);
      return;
    }
    void refresh();
  }, [refresh]);

  /**
   * Any 401 from a non-auth route means the session we are holding is dead —
   * expired, revoked, or pointing at a database that is no longer there. Drop
   * the user so the app returns to sign-in, instead of leaving every button on
   * screen to fail with a misleading error.
   */
  useEffect(() => {
    const onUnauthorized = (): void => setUser(null);
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const signOut = useCallback(async () => {
    await api.logout().catch(() => {});
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, capabilities, loading, error, refresh, setUser, signOut, travel, setTravel }),
    [user, capabilities, loading, error, refresh, signOut, travel],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}

/** The browser's own timezone — used so "today" matches the user's felt day. */
export function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}
